import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../ui/Button'
import { CheckSquare, Save, Send, ShieldCheck, Users, Trash2, PlusCircle, ListChecks, SlidersHorizontal, Type, AlertCircle, ArrowLeft } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import Input from '../ui/Input'
import { Textarea } from '../ui/Textarea'
import { Label } from '../ui/Label'
import { Switch } from '../ui/Switch'
import Badge from '../ui/Badge'
import { toast } from 'react-toastify'
import { createSurvey, updateSurvey, fetchSurveyById, fetchModelById } from '../../api/survey.api'
import { fetchParticipantProfiles, createParticipant } from '../../api/participantApi'
import logger from '../../utils/logger'
import AddParticipantModal from './AddParticipantModal'

const questionTypeConfig = {
  SINGLE_CHOICE: { label: "QCM", icon: ListChecks, color: "bg-primary/10 text-primary" },
  SCALE: { label: "Échelle", icon: SlidersHorizontal, color: "bg-accent/10 text-accent" },
  TEXT: { label: "Texte libre", icon: Type, color: "bg-success/10 text-success" },
};

const CreateSurvey = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const modelId = searchParams.get('modelId');
  const isEditing = !!id;
  const isFromModel = !!modelId && !isEditing;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'INTERNAL',
    start_date: '',
    end_date: '',
    expected_participants: '',
    is_anonymous: false,
  });
  const [questions, setQuestions] = useState([]);
  const [nextId, setNextId] = useState(1);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);
  const [participantsSearch, setParticipantsSearch] = useState('');
  const [loading, setLoading] = useState(isEditing || isFromModel);
  const [submitting, setSubmitting] = useState(false);
  const [finalConfig, setFinalConfig] = useState({
    sendInvitations: true,
    saveAsTemplate: false,
  });
  const [addParticipantModal, setAddParticipantModal] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [modelName, setModelName] = useState('');
  
  // Utiliser un ref pour tracker les IDs déjà chargés
  const loadedIdsRef = useRef(new Set());

  // Charger les participants et le sondage (si édition)
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      // Vérifier si on a déjà chargé ce sondage
      const cacheKey = isEditing ? `survey-${id}` : isFromModel ? `model-${modelId}` : 'new-survey';
      if (loadedIdsRef.current.has(cacheKey)) {
        setLoading(false);
        return;
      }

      try {
        const profiles = await fetchParticipantProfiles();
        if (!isMounted) return;
        setAllParticipants(Array.isArray(profiles) ? profiles : []);

        // Charger un modèle si modelId est présent
        if (isFromModel && modelId) {
          const model = await fetchModelById(modelId);
          if (!isMounted) return;
          
          logger.info('Modèle chargé:', model);
          
          // Pré-remplir le formulaire avec les données du modèle
          setFormData({
            title: `${model.title} - Copie`,
            description: model.description || '',
            category: model.category || 'INTERNAL',
            start_date: '',
            end_date: '',
            expected_participants: '',
            is_anonymous: false,
          });
          
          // Stocker le nom du modèle pour l'affichage du badge
          setModelName(model.title);
          
          // Charger les questions du modèle
          if (model.questions && Array.isArray(model.questions)) {
            const loadedQuestions = model.questions.map((q, idx) => {
              // Extraire les options/choices
              const choices = q.choices || q.options || [];
              const optionsArray = choices.map(c => c.text || c.option_text || c);
              
              return {
                id: idx + 1,
                type: q.question_type || q.type || 'TEXT',
                text: q.text || q.question_text || '',
                required: q.is_required !== undefined ? q.is_required : true,
                multiple: q.question_type === 'MULTIPLE_CHOICE',
                options: optionsArray.length > 0 ? optionsArray : undefined,
              };
            });
            
            setQuestions(loadedQuestions);
            setNextId(loadedQuestions.length + 1);
            
            logger.info('Questions chargées:', loadedQuestions);
          }
        }
        
        // Charger un sondage existant si édition
        if (isEditing) {
          const survey = await fetchSurveyById(id);
          if (!isMounted) return;
          
          setFormData({
            title: survey.title,
            description: survey.description,
            category: survey.category,
            start_date: survey.start_date,
            end_date: survey.end_date,
            expected_participants: survey.expected_participants || '',
            is_anonymous: survey.is_anonymous,
          });
          
          // Charger les questions
          if (survey.questions && Array.isArray(survey.questions)) {
            const loadedQuestions = survey.questions.map(q => ({
              id: q.id,
              type: q.question_type,
              text: q.text,
              required: q.is_required,
              options: (q.choices || []).map(c => c.text),
            }));
            setQuestions(loadedQuestions);
            setNextId(Math.max(...loadedQuestions.map(q => q.id), 0) + 1);
          }

          // Charger les participants sélectionnés
          if (survey.participants && Array.isArray(survey.participants)) {
            // Extraire les IDs des participants (gérer les deux formats : objets ou IDs)
            const participantIds = survey.participants.map(p => 
              typeof p === 'object' ? (p.source_participant_id ?? p.id) : p
            );
            setSelectedParticipants(participantIds);
          }
        }

        // Marquer comme chargé
        loadedIdsRef.current.add(cacheKey);
      } catch (err) {
        if (!isMounted) return;
        logger.error('Erreur lors du chargement:', err);
        if (isEditing) {
          toast.error('Impossible de charger le sondage');
          navigate('/gerant/gestion-sondages');
        } else if (isFromModel) {
          toast.error('Impossible de charger le modèle');
          navigate('/gerant/modeles');
        } else {
          toast.error('Impossible de charger les participants');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, isEditing, modelId, isFromModel, navigate]);

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Le titre du sondage est obligatoire');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('La description est obligatoire');
      return false;
    }
    if (!formData.category) {
      toast.error('Sélectionnez une catégorie');
      return false;
    }
    if (!formData.start_date) {
      toast.error('La date de début est obligatoire');
      return false;
    }
    if (!formData.end_date) {
      toast.error('La date de fin est obligatoire');
      return false;
    }
    if (formData.start_date > formData.end_date) {
      toast.error('La date de fin doit être après la date de début');
      return false;
    }
    if (questions.length === 0) {
      toast.error('Ajoutez au moins une question');
      return false;
    }
    if (selectedParticipants.length === 0) {
      toast.error('Sélectionnez au moins un participant');
      return false;
    }
    return true;
  };

  const buildPayload = useCallback((isActive) => ({
    title: formData.title.trim(),
    description: formData.description.trim(),
    category: formData.category,
    start_date: formData.start_date,
    end_date: formData.end_date,
    is_anonymous: formData.is_anonymous,
    is_active: isActive,
    questions: questions.map((q, idx) => ({
      text: q.text.trim(),
      question_type: q.type,
      is_required: q.required,
      order: idx,
      choices: (q.options || []).map((opt, oi) => ({
        text: opt,
        order: oi
      }))
    })),
    participants: selectedParticipants,
    send_invitations: isActive && finalConfig.sendInvitations,
    is_template: finalConfig.saveAsTemplate,
  }), [formData, questions, selectedParticipants, finalConfig]);

  const handleSaveDraft = useCallback(async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre du sondage est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload(false);
      if (isEditing) {
        await updateSurvey(id, payload);
        toast.success('Brouillon mis à jour avec succès');
      } else {
        await createSurvey(payload);
        toast.success('Brouillon sauvegardé avec succès');
      }
      navigate('/gerant/gestion-sondages', { state: { refresh: true } });
    } catch (err) {
      logger.error('Erreur lors de la sauvegarde:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, isEditing, id, navigate]);

  const handlePublish = useCallback(async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const payload = buildPayload(true);
      if (isEditing) {
        await updateSurvey(id, payload);
        toast.success('Sondage mis à jour avec succès');
      } else {
        await createSurvey(payload);
        toast.success('Sondage publié avec succès');
      }
      navigate('/gerant/gestion-sondages', { state: { refresh: true } });
    } catch (err) {
      logger.error('Erreur lors de la publication:', err);
      toast.error('Erreur lors de la publication');
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, buildPayload, isEditing, id, navigate]);

  const addQuestion = (type) => {
    setQuestions([...questions, {
      id: nextId, 
      type, 
      text: "", 
      required: true,
      multiple: type === "SINGLE_CHOICE" ? false : undefined,
      options: type === "SINGLE_CHOICE" ? ["Option 1", "Option 2"] : undefined,
    }]);
    setNextId(nextId + 1);
  };

  const updateQuestion = (id, updates) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const removeQuestion = (id) => {
    if (questions.length === 1) {
      toast.warning('Conservez au moins une question');
      return;
    }
    setQuestions(questions.filter(q => q.id !== id));
  };

  const addOption = (qId) => {
    const q = questions.find(q => q.id === qId);
    if (q?.options) updateQuestion(qId, { options: [...q.options, `Option ${q.options.length + 1}`] });
  };

  const removeOption = (qId, oi) => {
    const q = questions.find(q => q.id === qId);
    if (q?.options && q.options.length > 2) updateQuestion(qId, { options: q.options.filter((_, i) => i !== oi) });
  };

  const toggleParticipant = (id) => {
    setSelectedParticipants(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const selectAllParticipants = () => {
    const allIds = allParticipants.map(p => p.source_participant_id ?? p.id);
    setSelectedParticipants(prev => prev.length === allIds.length ? [] : allIds);
  };

  const filteredParticipants = allParticipants.filter(p => {
    const q = participantsSearch.toLowerCase();
    if (!q) return true;
    return (
      (p.prenom || '').toLowerCase().includes(q) ||
      (p.nom || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q)
    );
  });

  const handleAddParticipant = async (formData) => {
    setAddingParticipant(true);
    try {
      const newParticipant = await createParticipant(formData);
      setAllParticipants([...allParticipants, newParticipant]);
      const participantId = newParticipant.source_participant_id ?? newParticipant.id;
      setSelectedParticipants([...selectedParticipants, participantId]);
      toast.success('Participant ajouté avec succès');
      setAddParticipantModal(false);
    } catch (err) {
      logger.error('Erreur lors de l\'ajout du participant:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Erreur lors de l\'ajout du participant';
      toast.error(errorMsg);
    } finally {
      setAddingParticipant(false);
    }
  };

  return (
    <>
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />Retour
      </Button>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-2xl font-bold text-foreground">
                  {isEditing ? 'Modifier le sondage' : 'Créer un sondage'}
                </h1>
                {isFromModel && modelName && (
                  <Badge variant="outline" className="gap-1.5 py-1 px-3">
                    Basé sur : {modelName}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {isEditing ? 'Mettez à jour votre sondage' : isFromModel ? 'Personnalisez et complétez votre sondage' : 'Configurez votre sondage et ajoutez des questions'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="gap-2"
                disabled={submitting}
                onClick={handleSaveDraft}
              >
                <Save className="h-4 w-4" />Brouillon
              </Button>
              <Button 
                className="gap-2 font-semibold"
                disabled={submitting}
                onClick={handlePublish}
              >
                <Send className="h-4 w-4" />{isEditing ? 'Mettre à jour' : 'Publier'}
              </Button>
            </div>
          </div>

      {/* Survey info */}
      <Card className="shadow-card border-0">
        <CardHeader><CardTitle className="font-display">Informations générales</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titre du sondage *</Label>
            <Input 
              id="title" 
              placeholder="Ex: Satisfaction client Q1 2026"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Description *</Label>
            <Textarea 
              id="desc" 
              placeholder="Décrivez l'objectif de ce sondage..." 
              rows={3}
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <select 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={formData.category}
                onChange={e => setFormData({...formData, category: e.target.value})}
              >
                <option value="INTERNAL">Interne</option>
                <option value="EXTERNAL">Externe</option>
                <option value="EVENT">Événementiel</option>
                <option value="SATISFACTION">Satisfaction</option>
                <option value="FEEDBACK">Feedback</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date de début *</Label>
              <Input 
                type="date"
                value={formData.start_date}
                onChange={e => setFormData({...formData, start_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Date de fin *</Label>
              <Input 
                type="date"
                value={formData.end_date}
                onChange={e => setFormData({...formData, end_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Participants estimés</Label>
              <Input 
                type="number" 
                placeholder="200" 
                min={1}
                value={formData.expected_participants}
                onChange={e => setFormData({...formData, expected_participants: e.target.value})}
              />
            </div>
          </div>
          {/* Options */}
          <div className="flex flex-col sm:flex-row gap-6 pt-2">
            <div className="flex items-center gap-3">
              <Switch 
                id="anonymous" 
                checked={formData.is_anonymous} 
                onCheckedChange={checked => setFormData({...formData, is_anonymous: checked})}
              />
              <Label htmlFor="anonymous" className="flex items-center gap-1.5 cursor-pointer">
                <ShieldCheck className="h-4 w-4 text-primary" />Réponses anonymes
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch 
                id="invite" 
                checked={finalConfig.sendInvitations} 
                onCheckedChange={checked => setFinalConfig({...finalConfig, sendInvitations: checked})}
              />
              <Label htmlFor="invite" className="flex items-center gap-1.5 cursor-pointer">
                <Send className="h-4 w-4 text-accent" />Envoyer les invitations
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Participants selection */}
      <Card className="shadow-card border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-display flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />Participants à inviter *
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setAddParticipantModal(true)}
                className="gap-2"
              >
                <PlusCircle className="h-4 w-4" />Ajouter un participant
              </Button>
              <Button variant="outline" size="sm" onClick={selectAllParticipants}>
                {selectedParticipants.length === allParticipants.length ? "Tout désélectionner" : "Tout sélectionner"}
              </Button>
            </div>
            
          </div>
        </CardHeader>
        <CardContent>
      

          {/* Recherche dans les participants existants */}
          <div className="mb-4">
            <Label className="text-sm font-semibold mb-2 block">Ou sélectionner dans la liste</Label>
            <Input 
              placeholder="Rechercher un participant..."
              value={participantsSearch}
              onChange={e => setParticipantsSearch(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {filteredParticipants.map(p => {
              const participantId = p.source_participant_id ?? p.id;
              const selected = selectedParticipants.includes(participantId);
              return (
                <button key={participantId} type="button" onClick={() => toggleParticipant(participantId)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                    selected ? "border-primary bg-primary/5" : "border-input hover:border-primary/40"
                  }`}>
                  <div className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                    selected ? "border-primary bg-primary" : "border-muted-foreground/30"
                  }`}>
                    {selected && <CheckSquare className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                    {(p.prenom?.[0] || '') + (p.nom?.[0] || '')}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{p.prenom} {p.nom}</p>
                    <p className="text-xs text-muted-foreground">{p.email}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {selectedParticipants.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">{selectedParticipants.length} participant(s) sélectionné(s)</p>
          )}
        </CardContent>
      </Card>

      {/* Questions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-foreground">Questions ({questions.length})</h2>
          {questions.length === 0 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md">
              <AlertCircle className="h-3.5 w-3.5" />
              Au moins une question requise
            </div>
          )}
        </div>

        {questions.map((q, index) => {
          const config = questionTypeConfig[q.type];
          const Icon = config?.icon;
          return (
            <Card key={q.id} className="shadow-card border-0">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-2 pt-1">
                    <span className="text-xs font-medium text-muted-foreground">{index + 1}</span>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {Icon && <div className={`p-1.5 rounded-md ${config.color}`}><Icon className="h-3.5 w-3.5" /></div>}
                      <Badge variant="outline" className="text-xs">{config?.label}</Badge>
                      {q.type === "SINGLE_CHOICE" && (
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={q.multiple || false}
                            onChange={e => updateQuestion(q.id, { multiple: e.target.checked })} className="rounded border-input" />
                          Choix multiples
                        </label>
                      )}
                      <label className="flex items-center gap-1.5 ml-auto text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={q.required}
                          onChange={e => updateQuestion(q.id, { required: e.target.checked })} className="rounded border-input" />
                        Obligatoire
                      </label>
                    </div>
                    <Input placeholder="Saisissez votre question..." value={q.text}
                      onChange={e => updateQuestion(q.id, { text: e.target.value })} className="font-medium" />
                    {q.type === "SINGLE_CHOICE" && q.options && (
                      <div className="space-y-2 pl-4">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <div className={`h-4 w-4 ${q.multiple ? "rounded" : "rounded-full"} border-2 border-muted-foreground/30`} />
                            <Input value={opt} onChange={e => { const newOpts = [...q.options]; newOpts[oi] = e.target.value; updateQuestion(q.id, { options: newOpts }); }} className="h-8 text-sm" />
                            {q.options.length > 2 && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeOption(q.id, oi)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-primary text-xs gap-1" onClick={() => addOption(q.id)}>
                          <PlusCircle className="h-3 w-3" />Ajouter une option
                        </Button>
                      </div>
                    )}
                    {q.type === "SCALE" && (
                      <div className="flex items-center gap-2 pl-4">
                        <span className="text-xs text-muted-foreground">1</span>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <div key={n} className="h-8 w-8 rounded-md border border-input flex items-center justify-center text-sm text-muted-foreground">{n}</div>
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">5</span>
                      </div>
                    )}
                    {q.type === "TEXT" && <Textarea placeholder="Réponse libre du participant..." disabled rows={2} className="text-sm opacity-50" />}
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive/60 hover:text-destructive" onClick={() => removeQuestion(q.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        <Card className="border-dashed border-2 shadow-none">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground text-center mb-3">Ajouter une question</p>
            <div className="flex justify-center gap-3 flex-wrap">
              {Object.entries(questionTypeConfig).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <Button key={type} variant="outline" className="gap-2" onClick={() => addQuestion(type)}>
                    <Icon className="h-4 w-4" />{config.label}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
        </>
      )}
    </div>
    <AddParticipantModal
      isOpen={addParticipantModal}
      onClose={() => setAddParticipantModal(false)}
      onSubmit={handleAddParticipant}
      loading={addingParticipant}
    />
    </>
  )
}

export default CreateSurvey
